import { useNavigate } from 'react-router-dom';
import { Boxes, ChevronRight } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useOwnedZones, type OwnedZone } from '@/api/opennebula/zones';
import { getProviderDisplayName } from './provider-display-name';

const BASE = '/services/private-zones';

/**
 * Lists the infrastructure zones the signed-in user owns. Each row links to the
 * zone's resource dashboard (hosts, datastores, networks, capacity).
 */
export function PrivateZoneList() {
  const navigate = useNavigate();
  const { ownedZones, isLoading, isError, refetch } = useOwnedZones();

  const columns: ColumnDef<OwnedZone, unknown>[] = [
    {
      id: 'id',
      header: 'Zone ID',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.id}</span>,
    },
    { id: 'name', header: 'Name', cell: ({ row }) => row.original.name },
    {
      id: 'provider',
      header: 'Provider',
      cell: ({ row }) => <Badge tone="info">{getProviderDisplayName(row.original.provider_scope)}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      cell: () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--vn-fg-muted)' }}>
          Manage <ChevronRight size={14} />
        </span>
      ),
    },
  ];

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href="/services" onClick={(e) => { e.preventDefault(); navigate('/services'); }}>Services</BreadcrumbItem>
            <BreadcrumbItem current>Private Zones</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Private Zones"
        description="Infrastructure zones you own — hosts, datastores and networks."
      />
      <Section title="Your zones" description="Zones you have been assigned as owner.">
        {isError ? (
          <Alert
            tone="danger"
            title="Couldn't load zones"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            The zone service is temporarily unavailable.
          </Alert>
        ) : isLoading ? (
          <Skeleton style={{ height: 160 }} />
        ) : (
          <DataTable
            columns={columns}
            data={ownedZones}
            getRowId={(z) => String(z.id)}
            onRowClick={(z) => navigate(`${BASE}/${z.id}`)}
            emptyState={
              <EmptyState
                icon={<Boxes size={28} />}
                title="No private zones"
                description="You do not own any infrastructure zones yet. Contact your administrator to be assigned as a zone owner."
              />
            }
            footer={`Showing ${ownedZones.length} zone${ownedZones.length === 1 ? '' : 's'}`}
          />
        )}
      </Section>
    </Container>
  );
}

